import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import {
  HdsModal,
  HdsTextBody,
  HdsCardContainer,
  HdsCopySnippet,
  HdsAlert,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Loading, WithErrors } from 'core/components';
import { boxPadding } from 'core/modifiers';

const I18N_BASE = `components.page.access-control.service-principals.details.actions.generate-key`;

/**
 *
 * `ServicePrincipalKeysGenerateModal` is a shared component that generates a service principal key and provides the secret for organization and project level service principals.
 *
 *
 * ```
 * <ServicePrincipalKeysGenerateModal
 * />
 * ```
 *
 * @class ServicePrincipalKeysGenerateModal
 *
 */
export default class ServicePrincipalKeysGenerateModal extends Component {
  @service api;
  @service intl;

  @tracked clientId;
  @tracked clientSecret;

  get headerIcon() {
    return this.generateKey?.last?.error ? 'alert-diamond' : 'key';
  }

  get headerText() {
    if (this.generateKey?.last?.error) {
      return this.intl.t(`${I18N_BASE}.error-generating-key`);
    }

    if (this.generateKey.isRunning) {
      return this.intl.t(`${I18N_BASE}.generating-key`);
    }

    return this.intl.t(`${I18N_BASE}.title`);
  }

  generateKey = task(async () => {
    this.clientId = null;
    this.clientSecret = null;

    const {
      organizationId,
      projectId,
      id: principalId,
    } = this.args.model.servicePrincipal;
    let newKeyResponse;
    if (projectId) {
      newKeyResponse =
        await this.api.servicePrincipal.servicePrincipalsServiceCreateProjectServicePrincipalKey(
          organizationId,
          projectId,
          { organizationId, projectId, principalId }
        );
    } else {
      newKeyResponse =
        await this.api.servicePrincipal.servicePrincipalsServiceCreateOrganizationServicePrincipalKey(
          organizationId,
          {
            organizationId,
            principalId,
          }
        );
    }

    this.clientId = newKeyResponse.key.clientId;
    this.clientSecret = newKeyResponse.clientSecret;

    this.args.onSuccess({
      key: newKeyResponse.key,
    });
  });

  <template>
    <WithErrors @error={{this.generateKey.last.error}} as |FormError|>
      <HdsModal
        @color="neutral"
        @onClose={{@onClose}}
        {{didInsert this.generateKey.perform}}
        data-test-generate-key-modal
        as |M|
      >
        <M.Header @icon={{this.headerIcon}}>
          {{this.headerText}}
        </M.Header>
        <M.Body>
          {{#if this.generateKey.last.error}}
            <div data-test-generate-key-error>
              <FormError />
            </div>
          {{else if this.generateKey.isRunning}}
            <div {{boxPadding "xl"}}>
              <Loading as |L|>
                <L.Message>
                  {{t
                    "components.page.access-control.service-principals.details.actions.generate-key.generating-key"
                  }}
                </L.Message>
              </Loading>
            </div>
          {{else}}
            <HdsTextBody
              @color="primary"
              @size="200"
              @tag="p"
              {{boxPadding "0 0 md 0"}}
            >
              {{t
                "components.page.access-control.service-principals.details.actions.generate-key.description"
              }}
            </HdsTextBody>
            <HdsCardContainer @level="mid" @hasBorder={{true}}>
              <div {{boxPadding "md"}}>
                <HdsTextBody
                  @color="strong"
                  @size="300"
                  @tag="p"
                  @weight="medium"
                  {{boxPadding "0 0 0 2xs"}}
                >
                  {{t
                    "components.page.access-control.service-principals.details.actions.generate-key.labels.client-id"
                  }}
                </HdsTextBody>
                <HdsCopySnippet
                  @color="secondary"
                  @isFullWidth={{true}}
                  @textToCopy={{this.clientId}}
                  data-test-copy-card-client-id
                />
              </div>
            </HdsCardContainer>
            <div {{boxPadding "md 0 2xs 0"}}>
              <HdsAlert @color="warning" @type="compact" as |A|>
                <A.Description>
                  {{t
                    "components.page.access-control.service-principals.details.actions.generate-key.copy-key-notice"
                  }}
                </A.Description>
              </HdsAlert>
            </div>
            <HdsCardContainer @level="mid" @hasBorder={{true}}>
              <div {{boxPadding "md"}}>
                <HdsTextBody
                  @color="strong"
                  @size="300"
                  @tag="p"
                  @weight="medium"
                  {{boxPadding "0 0 0 2xs"}}
                >
                  {{t
                    "components.page.access-control.service-principals.details.actions.generate-key.labels.client-secret"
                  }}
                </HdsTextBody>
                <HdsCopySnippet
                  @color="secondary"
                  @isFullWidth={{true}}
                  @textToCopy={{this.clientSecret}}
                  data-test-copy-card-client-secret
                />
              </div>
            </HdsCardContainer>
          {{/if}}
        </M.Body>
        <M.Footer as |F|>
          <HdsButton
            @color="secondary"
            @text={{t "components.form.close"}}
            type="button"
            {{on "click" F.close}}
            data-test-generate-key-modal-close
          />
        </M.Footer>
      </HdsModal>
    </WithErrors>
  </template>
}
